import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

export interface PagerLabels {
  first: string;
  previous: string;
  next: string;
  last: string;
  /** Popisek konkrétní stránky pro screen readery, např. „Strana 3". */
  page?: (page: number) => string;
}

export interface PagerTestIds {
  root?: string;
  first?: string;
  previous?: string;
  next?: string;
  last?: string;
  info?: string;
}

export interface PagerProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  labels: PagerLabels;
  /** `boxed` = tlačítka s rámečkem (výchozí), `plain` = textová. */
  variant?: 'boxed' | 'plain';
  /**
   * Číslované stránky s oknem ±N kolem aktuální a elipsami (idiom patičky ListPage).
   * Bez čísel se mezi šipkami vykreslí `info` (idiom dropdownu Selectoru).
   */
  numbers?: boolean;
  /** Velikost okna číslovaných stránek kolem aktuální stránky. */
  numbersWindow?: number;
  /** Středový obsah místo čísel (např. „Strana 2 z 16"). */
  info?: ReactNode;
  /** Textové popisky šipek Předchozí/Další vedle symbolů ‹ ›. */
  prevNextText?: { previous: string; next: string };
  disabled?: boolean;
  testIds?: PagerTestIds;
  className?: string;
}

export function PagerNavButton({
  label,
  disabled,
  onClick,
  active,
  variant,
  testId,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  active?: boolean;
  variant: 'boxed' | 'plain';
  testId?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onClick()}
      disabled={disabled}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      data-testid={testId}
      className={cn(
        'text-phx-sm min-w-phx-control-lg inline-flex h-7 items-center justify-center transition-colors',
        variant === 'plain' &&
          'text-phx-text-2 hover:text-phx-text px-phx-1 cursor-pointer border-0 bg-transparent disabled:cursor-not-allowed disabled:opacity-40',
        variant === 'boxed' &&
          cn(
            'rounded-phx border-phx-border-2 bg-phx-surface px-phx-1 border',
            disabled
              ? 'cursor-not-allowed opacity-50'
              : 'hover:border-phx-border-3 hover:bg-phx-surface-2 hover:text-phx-text cursor-pointer'
          ),
        active && 'border-phx-primary bg-phx-primary-tint text-phx-primary font-semibold'
      )}
    >
      {children}
    </button>
  );
}

/**
 * Sdílený stránkovací idiom « ‹ … › » — používá ho patička ListPage (s čísly stránek)
 * i dropdown/modal Selectoru (s textovým info místo čísel). Viz anatomie ListPage 04 §2.
 */
export function Pager({
  currentPage,
  totalPages,
  onPageChange,
  labels,
  variant = 'boxed',
  numbers = false,
  numbersWindow = 2,
  info,
  prevNextText,
  disabled,
  testIds,
  className,
}: PagerProps) {
  const go = (page: number) => onPageChange(Math.min(totalPages, Math.max(1, page)));
  const atFirst = disabled || currentPage <= 1;
  const atLast = disabled || currentPage >= totalPages;

  const pages: number[] = [];
  if (numbers) {
    for (
      let p = Math.max(1, currentPage - numbersWindow);
      p <= Math.min(totalPages, currentPage + numbersWindow);
      p++
    ) {
      pages.push(p);
    }
  }

  return (
    <div className={cn('gap-phx-1 flex items-center', className)} data-testid={testIds?.root}>
      <PagerNavButton
        label={labels.first}
        disabled={atFirst}
        onClick={() => go(1)}
        variant={variant}
        testId={testIds?.first}
      >
        «
      </PagerNavButton>
      <PagerNavButton
        label={labels.previous}
        disabled={atFirst}
        onClick={() => go(currentPage - 1)}
        variant={variant}
        testId={testIds?.previous}
      >
        ‹{prevNextText ? <span className="px-phx-1">{prevNextText.previous}</span> : null}
      </PagerNavButton>

      {numbers ? (
        <>
          {pages[0] !== undefined && pages[0] > 1 ? (
            <span className="text-phx-text-faint px-phx-1">…</span>
          ) : null}
          {pages.map((p) => (
            <PagerNavButton
              key={p}
              label={labels.page ? labels.page(p) : String(p)}
              disabled={disabled}
              active={p === currentPage}
              onClick={() => go(p)}
              variant={variant}
            >
              {p}
            </PagerNavButton>
          ))}
          {pages.length > 0 && pages[pages.length - 1] < totalPages ? (
            <span className="text-phx-text-faint px-phx-1">…</span>
          ) : null}
        </>
      ) : (
        <span
          className="text-phx-sm text-phx-text-muted px-phx-2 min-w-[7.5rem] text-center whitespace-nowrap"
          data-testid={testIds?.info}
        >
          {info}
        </span>
      )}

      <PagerNavButton
        label={labels.next}
        disabled={atLast}
        onClick={() => go(currentPage + 1)}
        variant={variant}
        testId={testIds?.next}
      >
        {prevNextText ? <span className="px-phx-1">{prevNextText.next}</span> : null}›
      </PagerNavButton>
      <PagerNavButton
        label={labels.last}
        disabled={atLast}
        onClick={() => go(totalPages)}
        variant={variant}
        testId={testIds?.last}
      >
        »
      </PagerNavButton>
    </div>
  );
}
